import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiGet, apiPut } from "utils/apiFetch";
import apiPath from "utils/pathObj";
import useToastContext from "hooks/useToastContext";
import ConfirmationModal from "components/reusable/ConfirmationModal";

const StaffIpModal = ({ item, role, title, onClose }) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const userId = String(item?._id || "");
  const [policy, setPolicy] = useState(null);
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(null);

  const load = async () => {
    const res = await apiGet(apiPath.geoAccessPolicy);
    if (res?.data?.success) setPolicy(res.data.results);
    else notification.error(res?.data?.message || t("GEO_LOAD_FAILED"));
  };

  useEffect(() => {
    if (userId) load();
  }, [userId]);

  const rows = (policy?.ipAllowlist || []).filter(
    (i) => (i.role || "any") === role && String(i.userId || "") === userId,
  );
  const blockedRows = (policy?.ipBlocklist || []).filter(
    (i) => (i.role || "any") === role && String(i.userId || "") === userId,
  );

  const savePolicy = async (partial) => {
    try {
      setSaving(true);
      const res = await apiPut(apiPath.geoAccessPolicy, { ...policy, ...partial });
      if (res?.data?.success) {
        setPolicy(res.data.results);
        notification.success(res.data.message || t("GEO_SAVED"));
      } else notification.error(res?.data?.message || t("GEO_SAVE_FAILED"));
    } catch (err) {
      notification.error(err?.response?.data?.message || t("GEO_SAVE_FAILED"));
    }
    setSaving(false);
  };

  const addIp = async (e) => {
    e.preventDefault();
    const ip = value.trim();
    if (!ip || !policy) return;
    const list = policy.ipAllowlist || [];
    if (list.some((i) => i.value === ip && (i.role || "any") === role && String(i.userId || "") === userId)) {
      notification.error(t("GEO_IP_EXISTS"));
      return;
    }
    await savePolicy({
      ipAllowlist: [...list, { value: ip, label: label.trim(), role, userId }],
      ipBlocklist: (policy.ipBlocklist || []).filter(
        (i) => !(i.value === ip && (i.role || "any") === role && String(i.userId || "") === userId),
      ),
    });
    setValue("");
    setLabel("");
  };

  const blockIp = async (rawValue, rawLabel) => {
    const ip = String(rawValue || value).trim();
    if (!ip || !policy) return;
    const list = policy.ipBlocklist || [];
    if (list.some((i) => i.value === ip && (i.role || "any") === role && String(i.userId || "") === userId)) {
      notification.error(t("GEO_IP_EXISTS"));
      return;
    }
    await savePolicy({
      ipBlocklist: [...list, { value: ip, label: String(rawLabel || label).trim() || t("GEO_SECTION_BLOCKED"), role, userId }],
      ipAllowlist: (policy.ipAllowlist || []).filter(
        (i) => !(i.value === ip && (i.role || "any") === role && String(i.userId || "") === userId),
      ),
    });
    setValue("");
    setLabel("");
  };

  const removeIp = (entryValue) => {
    savePolicy({
      ipAllowlist: (policy?.ipAllowlist || []).filter(
        (i) => !(i.value === entryValue && (i.role || "any") === role && String(i.userId || "") === userId),
      ),
    });
  };

  const removeBlockedIp = (entryValue) => {
    savePolicy({
      ipBlocklist: (policy?.ipBlocklist || []).filter(
        (i) => !(i.value === entryValue && (i.role || "any") === role && String(i.userId || "") === userId),
      ),
    });
  };

  return (
    <div className="overflow-x-hidden overflow-y-auto fixed inset-0 justify-center items-center flex z-50 outline-none focus:outline-none bg-black/40">
      <div className="relative mx-auto max-w-lg w-full my-6 px-3">
        <div className="bg-white rounded-lg shadow-lg">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold">
              {title} — {item?.name || item?.email}
            </h3>
            <button type="button" className="text-2xl leading-none" onClick={onClose}>
              ×
            </button>
          </div>
          <form onSubmit={addIp} className="p-4 flex flex-wrap gap-2">
            <input
              className="border rounded-lg px-3 py-2 flex-1 min-w-[160px]"
              placeholder={t("GEO_IP_PLACEHOLDER")}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
            />
            <input
              className="border rounded-lg px-3 py-2 flex-1 min-w-[120px]"
              placeholder={t("GEO_LABEL")}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
            <button type="submit" className="bg-LightBlue text-white px-4 py-2 rounded-lg" disabled={saving}>
              {t("GEO_ADD_IP")}
            </button>
            <button
              type="button"
              className="border border-red-400 text-red-600 px-4 py-2 rounded-lg"
              disabled={saving}
              onClick={() => blockIp()}
            >
              {t("GEO_BLOCK_IP")}
            </button>
            {policy?.currentIp ? (
              <button
                type="button"
                className="border px-4 py-2 rounded-lg"
                disabled={saving}
                onClick={() => {
                  setValue(policy.currentIp);
                  setLabel(t("GEO_THIS_COMPUTER"));
                }}
              >
                {t("GEO_ALLOW_MY_IP")} ({policy.currentIp})
              </button>
            ) : null}
          </form>
          <div className="px-4 pb-4">
            {rows.length === 0 ? (
              <p className="text-sm text-gray-400">{t("GEO_STAFF_NO_IPS")}</p>
            ) : (
              rows.map((ip) => (
                <div
                  key={`${ip.userId}-${ip.value}`}
                  className="flex justify-between items-center border-b py-2 text-sm"
                >
                  <span>
                    {ip.value} {ip.label ? `— ${ip.label}` : ""}
                  </span>
                  <div className="flex gap-3">
                    <button type="button" className="text-red-600" onClick={() => blockIp(ip.value, ip.label)}>
                      {t("GEO_BLOCK_IP")}
                    </button>
                    <button type="button" className="text-red-500" onClick={() => setRemoveConfirm({ name: ip.value, run: () => removeIp(ip.value) })}>
                      Remove
                    </button>
                  </div>
                </div>
              ))
            )}
            <h4 className="font-semibold mt-4 mb-1">{t("GEO_SECTION_BLOCKED")}</h4>
            <p className="text-sm text-gray-500 mb-2">{t("GEO_SECTION_BLOCKED_HELP")}</p>
            {blockedRows.length === 0 ? (
              <p className="text-sm text-gray-400">{t("GEO_NONE")}</p>
            ) : (
              blockedRows.map((ip) => (
                <div
                  key={`block-${ip.value}`}
                  className="flex justify-between items-center border-b py-2 text-sm"
                >
                  <span>
                    {ip.value} {ip.label ? `— ${ip.label}` : ""}
                  </span>
                  <button type="button" className="text-red-500" onClick={() => setRemoveConfirm({ name: ip.value, run: () => removeBlockedIp(ip.value) })}>
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={!!removeConfirm}
        onClose={() => setRemoveConfirm(null)}
        onConfirm={() => {
          removeConfirm?.run?.();
          setRemoveConfirm(null);
        }}
        isDanger
        title="Remove?"
        message={`Do you still want to remove ${removeConfirm?.name || "this"}?`}
        confirmText="Yes, remove"
        cancelText="Cancel"
      />
    </div>
  );
};

export default StaffIpModal;
